import {
  HookFormErrorMessage,
  Icon,
  Input,
  Modal,
  Stack,
  Text,
  useToast,
} from '@pairy/pds-react';
import { useForm } from 'react-hook-form';
import { FORM_VALIDATIONS } from '@/../../packages/next-pdf-viewer/src/constant/formValidations';

import { SetState } from '@/types/helper';
import useFileIndividualList from '@/hooks/queries/file/useFileIndividualList';
import useCreateIndividualLink from '@/hooks/mutations/file/useCreateIndividualLink';

import * as Styled from './index.styles';
import OneToOneLinkList from './OneToOneLinkList';

interface Inputs {
  userEmail: string;
}

interface Props {
  fileId: number;
  isModalOpen: boolean;
  setIsModalOpen: SetState<boolean>;
}

const OneToOneLinkGenerateModal = ({
  fileId,
  isModalOpen,
  setIsModalOpen,
}: Props) => {
  const toast = useToast();

  const {
    register,
    getValues,
    resetField,
    formState: { isValid, errors },
  } = useForm<Inputs>({
    mode: 'all',
  });

  const { data, refetch } = useFileIndividualList(
    { fileId },
    {
      enabled: Boolean(fileId),
      select: (data) => data.data.data,
    },
  );

  const { mutate: createIndividualLink } = useCreateIndividualLink({
    onSuccess: () => {
      refetch();
      resetField('userEmail');
      toast('링크가 생성되었습니다.');
    },
  });

  const handleCreateIndividualLink = () => {
    createIndividualLink({ fileId, email: getValues('userEmail') });
  };

  const checkUserEmailInputDuplicate = (value: string) => {
    if (data) {
      const duplicateItem = data.find((item) => item.email === value);
      if (typeof duplicateItem !== 'undefined') {
        return '이미 링크가 생성된 이메일입니다';
      }
    }
    return true;
  };

  return (
    <Modal.Root
      open={isModalOpen}
      onOpenChange={(open) => {
        setIsModalOpen(open);
      }}
    >
      <Modal.Content>
        <Modal.Header title="1:1 공유용 링크 생성하기" />
        <Modal.Body>
          <Stack gap={16} direction="column">
            <Text variant="md-semibold">
              파일을 공유받을 분의 이메일 주소를 입력해주세요.
            </Text>

            <Stack gap={8} width="100%">
              <Styled.InputWrapper direction="column">
                <Input
                  width="100%"
                  type="email"
                  isError={Boolean(errors.userEmail)}
                  {...register('userEmail', {
                    pattern: {
                      value: FORM_VALIDATIONS.email.pattern.value,
                      message: '올바른 이메일 형식을 입력해주세요',
                    },
                    required: true,
                    validate: checkUserEmailInputDuplicate,
                  })}
                />
                {errors.userEmail && (
                  <HookFormErrorMessage errors={errors} name="userEmail" />
                )}
              </Styled.InputWrapper>
              <Styled.CreateButton
                variant="skyblue_300"
                paddingX={16}
                paddingY={8}
                disabled={!isValid}
                width="fit-content"
                onClick={handleCreateIndividualLink}
                leftIcon={<Icon icon="ph:link" color="inherit" size={17} />}
              >
                1:1 전달용 링크 생성하기
              </Styled.CreateButton>
            </Stack>

            {data && data.length >= 1 && (
              <OneToOneLinkList
                fileId={fileId}
                individualLinkList={data}
                refetchIndividualLinkList={() => refetch()}
              />
            )}
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export default OneToOneLinkGenerateModal;
